/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import once from 'lodash/once';
import classNames from 'classnames';

import { currencyString } from '../../util/currency';
import Button from '../../components/Button/Button';
import ButtonLink from '../../components/ButtonLink/ButtonLink';
import ContactUsContent from '../../components/ContactUsContent/ContactUsContent';
import TextInput from '../../components/TextInput/TextInput';
import { getPensionExpert } from '../../redux/selectors/investmentAdvice';
import {
  contributionAmountStepVisitedAction,
  ongoingContributionAmountStepVisitedAction,
  returnLinkClickedAction,
  ongoingReturnLinkClickedAction,
  amountManuallyChangedAction,
  ongoingAmountManuallyChangedAction,
  taxReliefLinkClickedAction,
  ongoingTaxReliefLinkClickedAction,
  confirmClickedAction,
  ongoingConfirmClickedAction,
  nextClickedAction,
  ongoingNextClickedAction,
  contributionAmountStepMessageMeClickedAction,
  ongoingContributionAmountStepMessageMeClickedAction,
  contributionFixedAmountFiftyClickedAction,
  ongoingContributionFixedAmountFiftyClickedAction,
  contributionFixedAmountOneHundredClickedAction,
  ongoingContributionFixedAmountOneHundredClickedAction,
  contributionFixedAmountOneHundredFiftyClickedAction,
  ongoingContributionFixedAmountOneHundredFiftyClickedAction,
} from '../../redux/modules/contributions';
import { getIsCustomerOngoing } from '../../redux/selectors/experience';
import ProgressStepper from '../../components/ProgressStepper/ProgressStepper';
import BaseGrid from '../../components/BaseGrid/BaseGrid';
import { TAX_RELIEF_URL } from '../../config';

import ContributionsAmountImage from '../../assets/images/contributions/contributions-amount.svg';

import styles from './SetupContributionPage.css';

const ContributionAmountStep = ({
  setFormData,
  setStep,
  formData,
  amendMode,
  step,
  totalSteps,
  dispatchContributionAmountStepVisited,
  dispatchReturnLinkClicked,
  dispatchAmountManuallyChanged: _dispatchAmountManuallyChanged,
  dispatchTaxReliefLinkClicked,
  dispatchNextClicked,
  dispatchConfirmClicked,
  dispatchMessageMeClicked,
  dispatchFixedAmountFiftyClicked,
  dispatchFixedAmountOneHundred,
  dispatchFixedAmountOneHundredFiftyClicked,
  previousContributionAmount,
  contactUsMessageInlineLabel,
}) => {
  const previousContributions = previousContributionAmount ? parseInt(previousContributionAmount, 10) : 0;
  const [buttonSelected, setButtonSelected] = useState(formData.amountPerMonth);
  const [dispatchAmountManuallyChanged] = useState(() => once(_dispatchAmountManuallyChanged));
  const taxRelief = formData.amountPerMonth ? Math.round(formData.amountPerMonth * 0.25) : 0;
  const totalContribution = formData.amountPerMonth + taxRelief;
  const shouldAskAdditionalAmlQuestions = formData.amountPerMonth > 800 && (
    !formData.occupation || (!formData.salary && formData.salary !== 0)
  );
  const errorMessage = 'You are already contributing the selected amount into your pension, please select a different amount.';

  const changeContribution = (amount) => {
    setButtonSelected(amount);
    setFormData({
      ...formData,
      amountPerMonth: amount,
      previousContributionAmount: previousContributions,
      amountPerMonthMask: '',
    });
  };

  const onOtherAmountInputChanged = (amount) => {
    const amountPerMonth = parseInt(amount, 10);
    const amountPerMonthMask = currencyString(amountPerMonth);
    setButtonSelected(amountPerMonth);
    setFormData({
      ...formData,
      amountPerMonth,
      amountPerMonthMask,
      previousContributionAmount: previousContributions,
    });
  };

  useEffect(() => {
    function prePopulateAmount() {
      const urlParams = new URLSearchParams(window.location.search);
      let previousAmount = null;

      if (urlParams.has('amount')) {
        previousAmount = parseInt(urlParams.get('amount'), 10);
      }
      if (previousContributions > 0) {
        previousAmount = previousContributions;
      }
      if (previousAmount) {
        try {
          if (previousAmount === 50 || previousAmount === 100 || previousAmount === 150) {
            changeContribution(previousAmount);
          } else {
            onOtherAmountInputChanged(previousAmount);
          }
        } catch (e) {
          console.error(e);
        }
      }
    }

    prePopulateAmount();
    window.scrollTo(0, 0);
    dispatchContributionAmountStepVisited();
  }, []);

  return (
    <BaseGrid pageSectionStyles={{ marginBottom: '200px' }} addPensionsTheme>
      <div className={styles.contributionsContainer}>
        <div className={styles.progressContainer}>
          <ProgressStepper maxStep={totalSteps} step={step + 1} />
        </div>
        {!amendMode && (
          <ButtonLink
            secondary
            mid
            onClick={() => {
              dispatchReturnLinkClicked();
            }}
            back
            label="← Back"
          />
        )}
        <div className={styles.imageContainer}>
          <img src={ContributionsAmountImage} alt="watering plants" />
        </div>
        <h2 className={styles.contributionsHeading}>{'How much would you like to contribute each month?'}</h2>
        <div className={styles.contributionAmountWrapper}>
          <div className={classNames(styles.contributionRowContainer, styles.contributionAmountButtonsContainer)}>
            <button
              className={classNames(styles.contributionAmountButton, { [styles.buttonSelected]: buttonSelected === 50 })}
              type="button"
              onClick={() => {
                dispatchFixedAmountFiftyClicked();
                changeContribution(50);
              }}
            >
              {'£50'}
            </button>
            <button
              className={classNames(styles.contributionAmountButton, { [styles.buttonSelected]: buttonSelected === 100 })}
              type="button"
              onClick={() => {
                dispatchFixedAmountOneHundred();
                changeContribution(100);
              }}
            >
              {'£100'}
            </button>
            <button
              className={classNames(styles.contributionAmountButton, { [styles.buttonSelected]: buttonSelected === 150 })}
              type="button"
              onClick={() => {
                dispatchFixedAmountOneHundredFiftyClicked();
                changeContribution(150);
              }}
            >
              {'£150'}
            </button>
          </div>
          <div className={styles.contributionRowContainer}>
            <p className={styles.contributionOr}>{'or'}</p>
          </div>
          <div className={styles.contributionRowContainer}>
            <TextInput
              value={formData.amountPerMonthMask}
              placeholder={'£ Other amount'}
              onChange={(event) => {
                dispatchAmountManuallyChanged();
                const strippedInput = event.target.value.replace(/\D/g, '');
                if (strippedInput) {
                  onOtherAmountInputChanged(strippedInput);
                } else {
                  setFormData({ ...formData, amountPerMonthMask: '', amountPerMonth: 0 });
                }
              }}
            />
          </div>
        </div>
        <div className={styles.standardContainer}>
          <h5 className={styles.summaryHeading}>{'Summary'}</h5>
          <div className={styles.contributionDetailLine}>
            <div className={styles.body}>{'Your contribution'}</div>
            <div className={styles.largeBody}>{currencyString(formData.amountPerMonth)}</div>
          </div>
          <div className={styles.contributionLineDivider} />
          <div className={styles.contributionDetailLine}>
            <div className={styles.body}>{'Tax relief'}</div>
            <div className={styles.largeBody}>{currencyString(taxRelief)}</div>
          </div>
          <div className={styles.contributionLineDivider} />
          <div className={styles.contributionDetailLine}>
            <div className={styles.bodyBold}>{'Total added to your pension'}</div>
            <div className={classNames(styles.bodyBold, styles.largeBody)}>{currencyString(totalContribution)}</div>
          </div>
          <div className={styles.contributionLineDivider} />
        </div>
        <div className={styles.standardContainer}>
          <a
            href={TAX_RELIEF_URL}
            className={styles.link}
            onClick={() => dispatchTaxReliefLinkClicked()}
            rel="noopener noreferrer"
            target="_blank"
          >
            {'Read more information on Tax Relief eligibility'}
          </a>
        </div>
        {formData.amountPerMonth === previousContributions && (
          <div className={styles.standardContainer}>
            <div className={styles.contributionAmountErrorMessage}>
              {errorMessage}
            </div>
          </div>
        )}

        <div className={styles.standardContainer}>
          <Button
            size="large"
            disabled={(!formData.amountPerMonth || formData.amountPerMonth === previousContributions)}
            label={(amendMode && !shouldAskAdditionalAmlQuestions) ? 'Confirm' : 'Next'}
            onClick={() => {
              if (amendMode && !shouldAskAdditionalAmlQuestions) {
                dispatchConfirmClicked();
                setStep(4);
              } else if (amendMode && shouldAskAdditionalAmlQuestions) {
                dispatchNextClicked();
                setStep(2);
              } else {
                dispatchNextClicked();
                setStep(step + 1);
              }
            }}
          />
        </div>
      </div>
      <div className={styles.pensionAdviserContainer}>
        <div className={styles.pensionAdviserCardWithHeader}>
          <ContactUsContent
            source={'ContributionAmountStep'}
            heading="Want to make a one-off contribution?"
            noExpertContentWrapper={({ children }) => (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                flexShrink: 'initial',
                padding: '10px',
                flexWrap: 'wrap',
              }}
              >
                {children}
              </div>
            )}
            showButton={false}
            customMessage={(
              <div>
                <div className={styles.contactUsInnerContentWithHeader}>
                  {'If you want to make a one-off personal contribution, '}
                  <ButtonLink
                    variant="primary"
                    label="click here"
                    to="/setup-lump-sum"
                    font="inherit"
                    onClick={dispatchMessageMeClicked}
                  />
                  {'.'}
                </div>
                <br />
                <div className={styles.contactUsInnerContentWithHeader}>
                  {'For contributions from your business, or if you have any questions, please '}
                  <span
                    style={{
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <ButtonLink
                      label={contactUsMessageInlineLabel}
                      variant="primary"
                      to="/inbox/new-message"
                      font="inherit"
                      onClick={dispatchMessageMeClicked}
                    />
                    {'.'}
                  </span>
                </div>
              </div>
            )}
          />
        </div>
      </div>
    </BaseGrid>
  );
};

const mapStateToProps = (state) => ({
  pensionAdviser: getPensionExpert(state),
  isCustomerOngoing: getIsCustomerOngoing(state),
});

const mapDispatchToProps = {
  _dispatchOnboardingContributionAmountStepVisited: contributionAmountStepVisitedAction,
  _dispatchOngoingContributionAmountStepVisited: ongoingContributionAmountStepVisitedAction,
  _dispatchOnboardingReturnLinkClicked: returnLinkClickedAction,
  _dispatchOngoingReturnLinkClicked: ongoingReturnLinkClickedAction,
  _dispatchOnboardingAmountManuallyChanged: amountManuallyChangedAction,
  _dispatchOngoingAmountManuallyChanged: ongoingAmountManuallyChangedAction,
  _dispatchOnboardingTaxReliefLinkClicked: taxReliefLinkClickedAction,
  _dispatchOngoingTaxReliefLinkClicked: ongoingTaxReliefLinkClickedAction,
  _dispatchOnboardingNextClicked: nextClickedAction,
  _dispatchOngoingNextClicked: ongoingNextClickedAction,
  _dispatchOnboardingConfirmClicked: confirmClickedAction,
  _dispatchOngoingConfirmClicked: ongoingConfirmClickedAction,
  _dispatchOnboardingMessageMeClicked: contributionAmountStepMessageMeClickedAction,
  _dispatchOngoingMessageMeClicked: ongoingContributionAmountStepMessageMeClickedAction,
  _dispatchFixedAmountFiftyClicked: contributionFixedAmountFiftyClickedAction,
  _dispatchOngoingFixedAmountFiftyClicked: ongoingContributionFixedAmountFiftyClickedAction,
  _dispatchFixedAmountOneHundredClicked: contributionFixedAmountOneHundredClickedAction,
  _dispatchOngoingFixedAmountOneHundredClicked: ongoingContributionFixedAmountOneHundredClickedAction,
  _dispatchFixedAmountOneHundredFiftyClicked: contributionFixedAmountOneHundredFiftyClickedAction,
  _dispatchOngoingFixedAmountOneHundredFiftyClicked: ongoingContributionFixedAmountOneHundredFiftyClickedAction,
};

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const { isCustomerOngoing } = stateProps;
  const {
    _dispatchOnboardingContributionAmountStepVisited,
    _dispatchOngoingContributionAmountStepVisited,
    _dispatchOnboardingReturnLinkClicked,
    _dispatchOngoingReturnLinkClicked,
    _dispatchOnboardingAmountManuallyChanged,
    _dispatchOngoingAmountManuallyChanged,
    _dispatchOnboardingTaxReliefLinkClicked,
    _dispatchOngoingTaxReliefLinkClicked,
    _dispatchOnboardingNextClicked,
    _dispatchOngoingNextClicked,
    _dispatchOnboardingConfirmClicked,
    _dispatchOngoingConfirmClicked,
    _dispatchOnboardingMessageMeClicked,
    _dispatchOngoingMessageMeClicked,
    _dispatchFixedAmountFiftyClicked,
    _dispatchOngoingFixedAmountFiftyClicked,
    _dispatchFixedAmountOneHundredClicked,
    _dispatchOngoingFixedAmountOneHundredClicked,
    _dispatchFixedAmountOneHundredFiftyClicked,
    _dispatchOngoingFixedAmountOneHundredFiftyClicked,
  } = dispatchProps;
  return {
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    dispatchContributionAmountStepVisited: isCustomerOngoing ? _dispatchOngoingContributionAmountStepVisited : _dispatchOnboardingContributionAmountStepVisited,
    dispatchReturnLinkClicked: isCustomerOngoing ? _dispatchOngoingReturnLinkClicked : _dispatchOnboardingReturnLinkClicked,
    dispatchAmountManuallyChanged: isCustomerOngoing ? _dispatchOngoingAmountManuallyChanged : _dispatchOnboardingAmountManuallyChanged,
    dispatchTaxReliefLinkClicked: isCustomerOngoing ? _dispatchOngoingTaxReliefLinkClicked : _dispatchOnboardingTaxReliefLinkClicked,
    dispatchNextClicked: isCustomerOngoing ? _dispatchOngoingNextClicked : _dispatchOnboardingNextClicked,
    dispatchConfirmClicked: isCustomerOngoing ? _dispatchOngoingConfirmClicked : _dispatchOnboardingConfirmClicked,
    dispatchMessageMeClicked: isCustomerOngoing ? _dispatchOngoingMessageMeClicked : _dispatchOnboardingMessageMeClicked,
    dispatchFixedAmountFiftyClicked: isCustomerOngoing ? _dispatchOngoingFixedAmountFiftyClicked : _dispatchFixedAmountFiftyClicked,
    dispatchFixedAmountOneHundred: isCustomerOngoing ? _dispatchOngoingFixedAmountOneHundredClicked : _dispatchFixedAmountOneHundredClicked,
    dispatchFixedAmountOneHundredFiftyClicked: isCustomerOngoing ? _dispatchOngoingFixedAmountOneHundredFiftyClicked : _dispatchFixedAmountOneHundredFiftyClicked,
  };
};

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(ContributionAmountStep);
